import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/api/client';
import { extractErrorMessage } from '@/api/errors';

type Audience = 'individual' | 'consultant' | 'store' | 'public';

const AUDIENCES: { key: Audience; label: string }[] = [
  { key: 'individual', label: 'Individuals' },
  { key: 'consultant', label: 'Consultants' },
  { key: 'store', label: 'Stores' },
  // Anonymous website visitors — the privacy notice on the landing page
  { key: 'public', label: 'Public (website)' },
];

interface TermsDocument {
  id: number;
  audience: Audience;
  version: string;
  title: string;
  content_markdown: string;
  requires_reacceptance: boolean;
  is_published: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

const EMPTY_DRAFT = {
  version: '',
  title: '',
  content_markdown: '',
  requires_reacceptance: true,
};

export function TermsPage() {
  const [audience, setAudience] = useState<Audience>('individual');
  const [docs, setDocs] = useState<TermsDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | 'new' | null>(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<TermsDocument[]>(
        '/back-office/terms-documents',
        {
          params: { audience },
        }
      );
      setDocs(res.data ?? []);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load terms documents'));
    } finally {
      setLoading(false);
    }
  }, [audience]);

  useEffect(() => {
    refresh();
    setSelectedId(null);
  }, [refresh]);

  const selectedDoc = useMemo(
    () =>
      typeof selectedId === 'number'
        ? (docs.find((d) => d.id === selectedId) ?? null)
        : null,
    [selectedId, docs]
  );

  useEffect(() => {
    if (selectedId === 'new') {
      setDraft(EMPTY_DRAFT);
    } else if (selectedDoc) {
      setDraft({
        version: selectedDoc.version,
        title: selectedDoc.title,
        content_markdown: selectedDoc.content_markdown,
        requires_reacceptance: selectedDoc.requires_reacceptance,
      });
    }
  }, [selectedId, selectedDoc]);

  const isEditingNew = selectedId === 'new';

  const onSave = async () => {
    if (
      !draft.version.trim() ||
      !draft.title.trim() ||
      !draft.content_markdown.trim()
    ) {
      setError('Version, title, and content are required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (isEditingNew) {
        const res = await api.post<TermsDocument>(
          '/back-office/terms-documents',
          {
            audience,
            version: draft.version.trim(),
            title: draft.title.trim(),
            content_markdown: draft.content_markdown,
            requires_reacceptance: draft.requires_reacceptance,
          }
        );
        await refresh();
        setSelectedId(res.data.id);
      } else if (selectedDoc) {
        await api.post(`/back-office/terms-documents/${selectedDoc.id}`, {
          version: draft.version.trim(),
          title: draft.title.trim(),
          content_markdown: draft.content_markdown,
          requires_reacceptance: draft.requires_reacceptance,
        });
        await refresh();
      }
    } catch (err) {
      setError(extractErrorMessage(err, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (label: string, fn: () => Promise<unknown>) => {
    setBusyAction(label);
    setError(null);
    try {
      await fn();
      await refresh();
    } catch (err) {
      setError(extractErrorMessage(err, `${label} failed`));
    } finally {
      setBusyAction(null);
    }
  };

  const onPublish = (id: number) =>
    runAction('Publish', () =>
      api.post(`/back-office/terms-documents/${id}/publish`)
    );
  const onUnpublish = (id: number) =>
    runAction('Unpublish', () =>
      api.post(`/back-office/terms-documents/${id}/unpublish`)
    );
  const onDelete = (id: number) => {
    if (!window.confirm('Delete this draft? This cannot be undone.')) return;
    runAction('Delete', () =>
      api.delete(`/back-office/terms-documents/${id}`)
    ).then(() => {
      if (selectedId === id) setSelectedId(null);
    });
  };

  return (
    <div className="p-6 space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">Terms & Conditions</h1>
        <p className="text-sm text-slate-500">
          Manage T&C documents per audience. Publishing a new version
          automatically retires the previous one. Mark{' '}
          <span className="font-medium">Requires re-acceptance</span> when the
          changes are material (legal content, fees) so existing users are
          prompted to accept again.
        </p>
      </header>

      <div className="flex gap-2 border-b border-slate-200">
        {AUDIENCES.map((a) => (
          <button
            key={a.key}
            type="button"
            onClick={() => setAudience(a.key)}
            className={
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px ' +
              (audience === a.key
                ? 'border-brand-500 text-brand-600'
                : 'border-transparent text-slate-500 hover:text-slate-700')
            }
          >
            {a.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-12 gap-4">
        <aside className="col-span-4 space-y-2">
          <button
            type="button"
            onClick={() => setSelectedId('new')}
            className="w-full rounded bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600"
          >
            + New version
          </button>

          {loading && <div className="text-sm text-slate-500">Loading…</div>}

          {!loading && docs.length === 0 && (
            <div className="rounded border border-dashed border-slate-300 p-4 text-sm text-slate-500">
              No documents yet. Create the first version for{' '}
              {AUDIENCES.find((a) => a.key === audience)?.label}.
            </div>
          )}

          <ul className="space-y-1">
            {docs.map((d) => {
              const active = selectedId === d.id;
              return (
                <li key={d.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(d.id)}
                    className={
                      'w-full text-left rounded border px-3 py-2 text-sm ' +
                      (active
                        ? 'border-brand-500 bg-brand-50'
                        : 'border-slate-200 hover:bg-slate-50')
                    }
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium truncate">{d.version}</span>
                      {d.is_published && (
                        <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-emerald-700">
                          Live
                        </span>
                      )}
                    </div>
                    <div className="truncate text-xs text-slate-500">
                      {d.title}
                    </div>
                    <div className="text-[11px] text-slate-400">
                      {d.requires_reacceptance
                        ? 'Force re-accept'
                        : 'Silent update'}{' '}
                      · Updated {new Date(d.updated_at).toLocaleDateString()}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        <section className="col-span-8 rounded border border-slate-200 bg-white">
          {!selectedId && (
            <div className="p-8 text-center text-sm text-slate-500">
              Select a document or create a new version to begin.
            </div>
          )}

          {(isEditingNew || selectedDoc) && (
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-xs font-medium text-slate-600">
                  Version
                  <input
                    type="text"
                    value={draft.version}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, version: e.target.value }))
                    }
                    placeholder="e.g. 2026-06-15.v1"
                    className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                  />
                </label>
                <label className="block text-xs font-medium text-slate-600">
                  Title
                  <input
                    type="text"
                    value={draft.title}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, title: e.target.value }))
                    }
                    placeholder="Swiftmedix Terms of Service"
                    className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                  />
                </label>
              </div>

              <label className="flex items-center gap-2 text-xs text-slate-700">
                <input
                  type="checkbox"
                  checked={draft.requires_reacceptance}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      requires_reacceptance: e.target.checked,
                    }))
                  }
                />
                Require existing users to re-accept (forces a popup on next app
                open).
              </label>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Content (Markdown)
                  </label>
                  <textarea
                    value={draft.content_markdown}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        content_markdown: e.target.value,
                      }))
                    }
                    rows={20}
                    className="w-full rounded border border-slate-300 px-2 py-1.5 font-mono text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Preview
                  </label>
                  <div className="prose-bo h-full max-h-[480px] overflow-y-auto rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                    <MarkdownPreview source={draft.content_markdown} />
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={onSave}
                  disabled={saving}
                  className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
                >
                  {saving
                    ? 'Saving…'
                    : isEditingNew
                      ? 'Save draft'
                      : 'Save changes'}
                </button>

                {selectedDoc && !selectedDoc.is_published && (
                  <button
                    type="button"
                    onClick={() => onPublish(selectedDoc.id)}
                    disabled={!!busyAction}
                    className="rounded bg-emerald-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
                  >
                    Publish
                  </button>
                )}

                {selectedDoc && selectedDoc.is_published && (
                  <button
                    type="button"
                    onClick={() => onUnpublish(selectedDoc.id)}
                    disabled={!!busyAction}
                    className="rounded border border-amber-400 px-3 py-1.5 text-sm font-medium text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                  >
                    Unpublish
                  </button>
                )}

                {selectedDoc && !selectedDoc.is_published && (
                  <button
                    type="button"
                    onClick={() => onDelete(selectedDoc.id)}
                    disabled={!!busyAction}
                    className="ml-auto rounded border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                  >
                    Delete
                  </button>
                )}
              </div>

              {selectedDoc && (
                <div className="text-xs text-slate-500">
                  {selectedDoc.is_published
                    ? `Published ${
                        selectedDoc.published_at
                          ? new Date(selectedDoc.published_at).toLocaleString()
                          : '—'
                      }`
                    : 'Draft — not yet visible to users.'}
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

// Tiny markdown renderer for the preview pane. Handles headings, paragraphs,
// unordered/ordered lists, bold, italic, code, hr, and tables — enough for
// legal documents. Not used at runtime in user apps (those render with a
// proper markdown lib).
function MarkdownPreview({ source }: { source: string }) {
  const html = useMemo(() => renderMd(source), [source]);
  return <div dangerouslySetInnerHTML={{ __html: html }} />;
}

function renderMd(src: string): string {
  if (!src) return '<p class="text-slate-400">Nothing to preview yet.</p>';

  const escape = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const inline = (s: string) =>
    s
      .replace(
        /`([^`]+)`/g,
        '<code class="rounded bg-slate-100 px-1 py-0.5 text-[0.85em] font-mono">$1</code>'
      )
      .replace(
        /\*\*([^*]+)\*\*/g,
        '<strong class="font-semibold text-slate-900">$1</strong>'
      )
      .replace(/\*([^*]+)\*/g, '<em>$1</em>')
      .replace(/_([^_]+)_/g, '<em>$1</em>')
      .replace(
        /\[([^\]]+)\]\(([^)]+)\)/g,
        '<a href="$2" target="_blank" rel="noreferrer" class="text-brand-600 underline-offset-2 hover:underline">$1</a>'
      );

  const H_CLASS: Record<number, string> = {
    1: 'mt-4 mb-2 text-xl font-semibold text-slate-900',
    2: 'mt-4 mb-2 text-lg font-semibold text-slate-900',
    3: 'mt-3 mb-1.5 text-base font-semibold text-slate-900',
    4: 'mt-3 mb-1 text-sm font-semibold text-slate-700 uppercase tracking-wide',
    5: 'mt-2 mb-1 text-sm font-medium text-slate-700',
    6: 'mt-2 mb-1 text-xs font-medium text-slate-500 uppercase tracking-wider',
  };

  const lines = src.split(/\r?\n/);
  const out: string[] = [];
  let inUl = false;
  let inOl = false;
  let inTable = false;
  let inQuote = false;
  let para: string[] = [];

  const flushPara = () => {
    if (para.length) {
      out.push(
        `<p class="mb-3 text-slate-700 leading-relaxed">${inline(
          escape(para.join(' '))
        )}</p>`
      );
      para = [];
    }
  };
  const closeLists = () => {
    if (inUl) {
      out.push('</ul>');
      inUl = false;
    }
    if (inOl) {
      out.push('</ol>');
      inOl = false;
    }
    if (inTable) {
      out.push('</tbody></table></div>');
      inTable = false;
    }
    if (inQuote) {
      out.push('</blockquote>');
      inQuote = false;
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) {
      flushPara();
      closeLists();
      continue;
    }
    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      flushPara();
      closeLists();
      const level = heading[1].length;
      out.push(
        `<h${level} class="${H_CLASS[level]}">${inline(
          escape(heading[2])
        )}</h${level}>`
      );
      continue;
    }
    if (/^---+$/.test(line)) {
      flushPara();
      closeLists();
      out.push('<hr class="my-4 border-slate-200" />');
      continue;
    }
    const quote = /^>\s+(.*)$/.exec(line);
    if (quote) {
      flushPara();
      if (inUl || inOl || inTable) closeLists();
      if (!inQuote) {
        out.push(
          '<blockquote class="my-3 border-l-2 border-brand-500 bg-slate-50 px-3 py-2 text-sm text-slate-600 italic">'
        );
        inQuote = true;
      }
      out.push(`<p class="mb-1 last:mb-0">${inline(escape(quote[1]))}</p>`);
      continue;
    }
    const li = /^[-*]\s+(.*)$/.exec(line);
    if (li) {
      flushPara();
      if (inOl || inTable || inQuote) closeLists();
      if (!inUl) {
        out.push(
          '<ul class="mb-3 list-disc pl-5 space-y-1 text-slate-700 marker:text-brand-500">'
        );
        inUl = true;
      }
      out.push(`<li>${inline(escape(li[1]))}</li>`);
      continue;
    }
    const oli = /^\d+\.\s+(.*)$/.exec(line);
    if (oli) {
      flushPara();
      if (inUl || inTable || inQuote) closeLists();
      if (!inOl) {
        out.push(
          '<ol class="mb-3 list-decimal pl-5 space-y-1 text-slate-700 marker:text-brand-500 marker:font-semibold">'
        );
        inOl = true;
      }
      out.push(`<li>${inline(escape(oli[1]))}</li>`);
      continue;
    }
    if (/^\|.*\|$/.test(line)) {
      flushPara();
      if (inUl || inOl || inQuote) closeLists();
      const cells = line
        .slice(1, -1)
        .split('|')
        .map((c) => c.trim());
      if (!inTable) {
        out.push(
          '<div class="mb-3 overflow-x-auto rounded-md border border-slate-200"><table class="w-full text-left text-sm"><thead class="bg-slate-50 text-slate-900"><tr>' +
            cells
              .map(
                (c) =>
                  `<th class="px-3 py-2 font-medium border-b border-slate-200">${inline(
                    escape(c)
                  )}</th>`
              )
              .join('') +
            '</tr></thead><tbody class="text-slate-700">'
        );
        inTable = true;
      } else if (/^\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?$/.test(line)) {
        // separator row — skip
      } else {
        out.push(
          '<tr class="border-b border-slate-100 last:border-b-0">' +
            cells
              .map((c) => `<td class="px-3 py-2">${inline(escape(c))}</td>`)
              .join('') +
            '</tr>'
        );
      }
      continue;
    }
    para.push(line.trim());
  }
  flushPara();
  closeLists();

  return out.join('\n');
}
